import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InfiniteScroll from 'react-infinite-scroll-component';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import NotificationService from 'services/notification.service';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import GiftOutlined from '@ant-design/icons/GiftOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',

  transform: 'none'
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const anchorRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await NotificationService.getUnreadCount();
      const count = res?.data?.unreadCount ?? res?.unreadCount ?? 0;
      setUnreadCount(Number(count) || 0);
    } catch (error) {
      // ignore UI noise if notifications are not available
      setUnreadCount(0);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setPage(1);
      setHasMore(true);
      const res = await NotificationService.listMyNotifications(1, 10);
      const list = res?.data?.items || [];
      setItems(Array.isArray(list) ? list : []);
      // Check if there are more items
      const total = res?.data?.total || list.length;
      setHasMore(list.length >= 10 && total > 10);
    } catch (error) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMoreNotifications = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    try {
      setIsLoadingMore(true);
      const nextPage = page + 1;
      const res = await NotificationService.listMyNotifications(nextPage, 10);
      const newItems = res?.data?.items || [];
      if (Array.isArray(newItems) && newItems.length > 0) {
        setItems((prev) => [...prev, ...newItems]);
        setPage(nextPage);
        // Check if there are more items
        const total = res?.data?.total || newItems.length;
        setHasMore(newItems.length >= 10 && (page + 1) * 10 < total);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      // silently fail
    } finally {
      setIsLoadingMore(false);
    }
  }, [page, hasMore, isLoadingMore]);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (open) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [open, fetchNotifications, fetchUnreadCount]);

  const formatRelativeTime = (value) => {
    if (!value) return 'just now';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return 'just now';
    const diffMs = Date.now() - dt.getTime();
    const mins = Math.floor(diffMs / (1000 * 60));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hr ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  const formatClock = (value) => {
    if (!value) return '--';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '--';
    return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const iconForType = useMemo(
    () => ({
      NEW_USER_REGISTERED: <MessageOutlined />,
      NEW_BOOKING_CREATED: <GiftOutlined />,
      NEW_VEHICLE_ADDED: <SettingOutlined />,
      NEW_BOOKING_RECEIVED: <GiftOutlined />,
      BOOKING_CONFIRMED: <CheckCircleOutlined />
    }),
    []
  );

  const markOneAsRead = async (id) => {
    if (!id) return;
    try {
      await NotificationService.markAsRead(id);
      setItems((prev) => prev.map((item) => (item._id === id ? { ...item, isRead: true } : item)));
      setUnreadCount((prev) => Math.max(0, prev - 1));

      // redirect if notification has a URL
      const item = items.find((i) => i._id === id);
      if (item?.redirectUrl) {
        window.open(item.redirectUrl);
      }
    } catch (error) {
      // noop
    }
  };

  const markAllAsRead = async () => {
    try {
      await NotificationService.markAllAsRead();
      setItems((prev) => prev.map((item) => ({ ...item, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      // noop
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  return (
    <Box sx={{ flexShrink: 0, mr: 2 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent',
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notification"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <>
                      {unreadCount > 0 && (
                        <Tooltip title="Mark as all read">
                          <IconButton color="success" size="small" onClick={markAllAsRead}>
                            <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                  <Box
                    id="notification-scrollable-list"
                    sx={{
                      maxHeight: 450,
                      overflowY: 'auto',
                      // Reserve gutter so right-side timestamp doesn't sit under the scrollbar.
                      scrollbarGutter: 'stable',
                      pr: 0.5
                    }}
                  >
                    {loading ? (
                      <List component="nav" sx={{ p: 0 }}>
                        <ListItem sx={{ justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={20} />
                        </ListItem>
                      </List>
                    ) : items.length ? (
                      <InfiniteScroll
                        dataLength={items.length}
                        next={fetchMoreNotifications}
                        hasMore={hasMore}
                        scrollableTarget="notification-scrollable-list"
                        loader={
                          <List component="nav" sx={{ p: 0 }}>
                            <ListItem sx={{ justifyContent: 'center', py: 1 }}>
                              <CircularProgress size={18} />
                            </ListItem>
                          </List>
                        }
                        endMessage={
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 1 }}>
                            No more notifications
                          </Typography>
                        }
                      >
                        <List
                          component="nav"
                          sx={{
                            p: 0,
                            '& .MuiListItemButton-root': {
                              py: 0.5,
                              px: 2,
                              pr: 3,
                              '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                              '& .MuiAvatar-root': avatarSX,
                              '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                            }
                          }}
                        >
                          {items.map((item) => {
                            const typeIcon = iconForType[item.type] || <SettingOutlined />;
                            return (
                              <ListItem
                                key={item._id}
                                component={ListItemButton}
                                divider
                                selected={!item.isRead}
                                onClick={() => markOneAsRead(item._id)}
                                secondaryAction={
                                  <Typography variant="caption" noWrap sx={{ mr: 1 }}>
                                    {formatClock(item.createdAt)}
                                  </Typography>
                                }
                                sx={{width: '100%'}}
                              >
                                <ListItemAvatar>
                                  <Avatar sx={{ color: 'primary.main', bgcolor: 'primary.lighter' }}>{typeIcon}</Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={<Typography variant="h6">{item.title || 'Notification'}</Typography>}
                                  secondary={item.message || formatRelativeTime(item.createdAt)}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </InfiniteScroll>
                    ) : (
                      <List component="nav" sx={{ p: 0 }}>
                        <ListItem sx={{ py: 2 }}>
                          <ListItemText primary={<Typography variant="body2">No notifications yet</Typography>} />
                        </ListItem>
                      </List>
                    )}
                  </Box>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
